import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Card from './ui/Card';
import Button from './ui/Button';
import { acceptInvitation, lookupInvitation } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, getIdToken, signOut } from 'firebase/auth';

function InvitationAcceptPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('idle'); // idle | working | success | error
    const [message, setMessage] = useState('');
    const [invitedEmail, setInvitedEmail] = useState('');
    const [institutionName, setInstitutionName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const token = searchParams.get('token');
    const { startSignup, cancelSignup } = useAuth();

    useEffect(() => {
        const init = async () => {
            if (!token) {
                setStatus('error');
                setMessage('Missing invitation token.');
                return;
            }
            try {
                setStatus('working');
                const info = await lookupInvitation(token);
                if (info.status !== 'pending') {
                    setStatus('error');
                    setMessage(`Invitation is ${info.status}.`);
                    return;
                }
                setStatus('idle');
                setInvitedEmail(info.email || '');
                setInstitutionName(info.institution?.name || '');
                setMessage(`You are invited as ${info.role} to ${info.institution?.name}.`);
            } catch (err) {
                setStatus('error');
                setMessage(err.message || 'Failed to validate invitation.');
            }
        };
        init();
    }, [token]);

    const handleAccept = async () => {
        if (!token || !invitedEmail) return;
        if (!password || password.length < 6) {
            setMessage('Please enter a password with at least 6 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setMessage('Passwords do not match.');
            return;
        }
        setStatus('working');
        setMessage('Processing your invitation...');
        try {
            // Prevent global auth fetches during this flow
            startSignup();
            // Try to create account
            let firebaseUser;
            try {
                const cred = await createUserWithEmailAndPassword(auth, invitedEmail, password);
                firebaseUser = cred.user;
            } catch (createErr) {
                // If already exists, try sign in
                if (createErr?.code === 'auth/email-already-in-use') {
                    const cred = await signInWithEmailAndPassword(auth, invitedEmail, password);
                    firebaseUser = cred.user;
                } else {
                    throw createErr;
                }
            }

            const idToken = await getIdToken(firebaseUser);
            await acceptInvitation(token, idToken);

            // Sign out to let the user log in explicitly
            try { await signOut(auth); } catch { }

            setStatus('success');
            setMessage('Invitation accepted. Please log in with your new account.');
            setTimeout(() => navigate('/pi-admin', { replace: true }), 1200);
        } catch (err) {
            setStatus('error');
            if (err?.code === 'auth/wrong-password') {
                setMessage('Wrong password for existing account. Please try again or use the login link below.');
            } else {
                setMessage(err.message || 'Failed to accept the invitation.');
            }
        } finally {
            // Resume normal auth state changes
            cancelSignup();
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <Card className="p-8 text-center">
                <h2 className="text-xl font-bold mb-4">Accept Invitation</h2>
                {status === 'working' && <p>Processing...</p>}
                {status !== 'working' && <p className="mb-4">{message || `You're invited to ${institutionName}. Create a password to accept your admin role.`}</p>}
                <div className="text-left space-y-3 max-w-sm mx-auto w-full">
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Email</label>
                        <div className="px-3 py-2 border border-gray-200 rounded bg-gray-50 text-sm text-gray-800">{invitedEmail || '...'}</div>
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Password</label>
                        <input type="password" className="w-full px-3 py-2 border border-gray-300 rounded" value={password} onChange={e => setPassword(e.target.value)} />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Confirm Password</label>
                        <input type="password" className="w-full px-3 py-2 border border-gray-300 rounded" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                    </div>
                    <div className="flex items-center justify-between pt-2">
                        <Link to={`/pi-admin?token=${encodeURIComponent(token)}`} className="text-blue-600 text-xs underline">I already have an account</Link>
                        <Button onClick={handleAccept} variant="primary">Accept Invitation</Button>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default InvitationAcceptPage;


